import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useGetTourOfferPricing, useSaveTourOfferPricing } from '~/hooks/apis/tours/useTourOffers';
import { useDownloadTourOptionPriceHistory } from '~/hooks/apis/tours/useTourOptionPriceHistory';

import { TourOfferPricing, TourOfferPricingDeparture } from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourPricingBulkApply from './NativeTourPricingBulkApply';
import NativeTourPricingLevelField from './NativeTourPricingLevelField';
import NativeTourPricingTable from './NativeTourPricingTable';
import {
  PricingRow,
  applyRoomBulk,
  changedPricingInput,
  changedProblemCount,
  hasOwnPricing,
  levelOf,
  roomTypeOrder,
} from './nativeTourPricing';

interface Props {
  tourOfferId: string;
}

const ALL = 'all';
const monthOf = (date: string) => dayjs(date).format('YYYY-MM');

/**
 * Discounts, markups and LUX+ prices for the whole tour, each option, each departure and each room.
 * The most specific one set wins. Saved on its own; display and member prices are worked out on save.
 */
export default function NativeTourDynamicPricingSection({ tourOfferId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error } = useGetTourOfferPricing(tourOfferId);
  const save = useSaveTourOfferPricing(tourOfferId);
  const download = useDownloadTourOptionPriceHistory();
  const [draft, setDraft] = useState<TourOfferPricing | null>(null);
  const [optionFilter, setOptionFilter] = useState(ALL);
  const [monthFilter, setMonthFilter] = useState(ALL);
  const [onlyOwn, setOnlyOwn] = useState(false);

  useEffect(() => {
    setDraft(data ?? null);
  }, [data]);

  const rows = useMemo<Array<PricingRow>>(
    () => (draft?.options ?? []).flatMap((option) => option.departures.map((departure) => ({ option, departure }))),
    [draft],
  );
  const months = useMemo(() => [...new Set(rows.map(({ departure }) => monthOf(departure.startDate)))].sort(), [rows]);
  const roomTypes = useMemo(
    () => roomTypeOrder([...new Set(rows.flatMap(({ departure }) => departure.prices.map((price) => price.roomType)))]),
    [rows],
  );
  const shown = rows.filter(
    ({ option, departure }) =>
      (optionFilter === ALL || option.tourOptionId === optionFilter) &&
      (monthFilter === ALL || monthOf(departure.startDate) === monthFilter) &&
      (!onlyOwn || hasOwnPricing(departure)),
  );

  if (isLoading || (data && !draft)) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Couldn't load the pricing: {error.message}</Alert>;
  }
  if (!draft) {
    return null;
  }

  const changes = changedPricingInput(draft, data);
  const isDirty = JSON.stringify(changes) !== JSON.stringify(changedPricingInput(data, data));
  const problemCount = changedProblemCount(draft, data);
  const editedPriceIds = new Set(
    changes.options.flatMap((option) =>
      option.departures.flatMap((departure) => departure.prices.map((price) => price.priceId)),
    ),
  );

  const updateDeparture = (tourOptionId: string, departure: TourOfferPricingDeparture) =>
    setDraft({
      ...draft,
      options: draft.options.map((option) =>
        option.tourOptionId === tourOptionId
          ? {
              ...option,
              departures: option.departures.map((other) =>
                other.departureId === departure.departureId ? departure : other,
              ),
            }
          : option,
      ),
    });

  const handleSave = async () => {
    try {
      await save.mutateAsync(changes);
      enqueueSnackbar('Pricing saved. Display and member prices are up to date.', { variant: 'success' });
    } catch (saveError) {
      enqueueSnackbar(`Couldn't save the pricing: ${saveError.message}`, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        The most specific setting wins: a room's, then its departure's, its option's, then the whole tour's. Leave a
        field blank to follow the level above. Display and member prices are worked out when you save. Only upcoming,
        available departures are listed.
      </Alert>

      <NativeTourCard
        title="Tour and options"
        description="The defaults every departure follows unless it has its own."
      >
        <Stack divider={<Divider flexItem />} spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box width={{ md: 220 }} flexShrink={0}>
              <Typography variant="subtitle2">Whole tour</Typography>
              <Typography variant="caption" color="text.secondary">
                Every option
              </Typography>
            </Box>
            <NativeTourPricingLevelField
              value={levelOf(draft)}
              onChange={(level) => setDraft({ ...draft, ...level })}
              followText="No adjustment"
            />
          </Stack>
          {draft.options.map((option) => (
            <Stack
              key={option.tourOptionId}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              <Box width={{ md: 220 }} flexShrink={0}>
                <Typography variant="subtitle2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.departures.length} upcoming departure{option.departures.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box flexGrow={1}>
                <NativeTourPricingLevelField
                  value={levelOf(option)}
                  onChange={(level) =>
                    setDraft({
                      ...draft,
                      options: draft.options.map((other) =>
                        other.tourOptionId === option.tourOptionId ? { ...other, ...level } : other,
                      ),
                    })
                  }
                  followText="Follow the tour"
                />
              </Box>
              <Button
                size="small"
                variant="text"
                startIcon={<DownloadIcon />}
                disabled={download.isPending}
                sx={{ flexShrink: 0 }}
                onClick={async () => {
                  try {
                    await download.mutateAsync({ tourOptionId: option.tourOptionId, name: option.name });
                  } catch (downloadError) {
                    enqueueSnackbar(`Couldn't download the price history: ${downloadError.message}`, {
                      variant: 'error',
                    });
                  }
                }}
              >
                Price history
              </Button>
            </Stack>
          ))}
        </Stack>
      </NativeTourCard>

      <NativeTourCard
        title="By departure"
        description="Each room's own discount, markup and LUX+, as in Dynamic Pricing V2. Open a departure for its own adjustment and LUX+ prices."
      >
        {rows.length === 0 ? (
          <Typography color="text.secondary">No upcoming available departures.</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                select
                size="small"
                label="Tour option"
                value={optionFilter}
                onChange={(event) => setOptionFilter(event.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={ALL}>All options</MenuItem>
                {draft.options.map((option) => (
                  <MenuItem key={option.tourOptionId} value={option.tourOptionId}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Month"
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value={ALL}>All months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {dayjs(`${month}-01`).format('MMMM YYYY')}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={<Switch checked={onlyOwn} onChange={(event) => setOnlyOwn(event.target.checked)} />}
                label="Only with a discount, markup or LUX+ set"
              />
            </Stack>

            <NativeTourPricingBulkApply
              roomTypes={roomTypes}
              shownCount={shown.length}
              onApply={(bulk) => {
                setDraft(applyRoomBulk(draft, new Set(shown.map(({ departure }) => departure.departureId)), bulk));
                enqueueSnackbar(`Applied to the ${shown.length} departures shown. Save to keep it.`, {
                  variant: 'info',
                });
              }}
            />
            <NativeTourPricingTable
              rows={shown}
              roomTypes={roomTypes}
              tourLevel={levelOf(draft)}
              isEdited={(priceId) => editedPriceIds.has(priceId)}
              onChange={updateDeparture}
            />
          </Stack>
        )}
      </NativeTourCard>

      <Paper
        variant="outlined"
        sx={{ p: 2, position: 'sticky', bottom: 16, zIndex: 1, display: isDirty ? 'block' : 'none' }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" disabled={save.isPending || problemCount > 0} onClick={handleSave}>
            Save pricing
          </Button>
          <Button variant="text" disabled={save.isPending} onClick={() => setDraft(data)}>
            Discard
          </Button>
          <Typography variant="body2" color={problemCount > 0 ? 'error' : 'text.secondary'}>
            {problemCount > 0
              ? `${problemCount} thing${problemCount === 1 ? '' : 's'} to fix first`
              : 'Unsaved pricing changes'}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}
